/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export interface ClinicAddress {
  readonly line1: string;
  readonly line2?: string | undefined;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string;
  readonly country: string;
}

export interface ClinicProfile {
  readonly id: string;
  readonly name: string;
  readonly legalName: string | null;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly gstin: string | null;
  readonly status: string;
  readonly timezone: string;
  readonly logoUrl: string | null;
  readonly address: ClinicAddress;
}

export interface NotificationPrefs {
  readonly sessionReminders: boolean;
  readonly paymentAlerts: boolean;
  readonly progressReports: boolean;
  readonly systemUpdates: boolean;
}

export interface UpdateClinicPayload {
  readonly name: string;
  readonly legalName?: string | undefined;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly timezone: string;
  readonly gstin?: string | undefined;
  readonly address: ClinicAddress;
}

export const clinicApi = {
  async getProfile(): Promise<ClinicProfile> {
    const { data } = await apiClient.get<ClinicProfile>('/clinics/me');
    return data;
  },
  async updateProfile(payload: UpdateClinicPayload): Promise<ClinicProfile> {
    const { data } = await apiClient.patch<ClinicProfile>('/clinics/me', payload);
    return data;
  },
  async getNotificationPrefs(): Promise<NotificationPrefs> {
    const { data } = await apiClient.get<NotificationPrefs>('/clinics/me/notifications');
    return data;
  },
  async updateNotificationPrefs(payload: NotificationPrefs): Promise<NotificationPrefs> {
    const { data } = await apiClient.patch<NotificationPrefs>('/clinics/me/notifications', payload);
    return data;
  },
};
